import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { useNavigate } from "react-router-dom";
import { UserPlus, UserCheck, UserMinus, X, Share2 } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { formatNumber } from "@/lib/utils";
import { useProfile } from "@/hooks/useProfile";

interface FollowsListProps {
  userId: string;
}

const ITEMS_PER_PAGE = 20;

const FollowsList = ({
 userId }: FollowsListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [requestsDialogOpen, setRequestsDialogOpen] = useState(false);
  const [followersDialogOpen, setFollowersDialogOpen] = useState(false);
  const [followingDialogOpen, setFollowingDialogOpen] = useState(false);
  const [requestsPage, setRequestsPage] = useState(0);
  const [followersPage, setFollowersPage] = useState(0);
  const [followingPage, setFollowingPage] = useState(0);

  // Fetch current user's profile to get username for share link
  const { data: currentUserProfile, isLoading: isLoadingProfile } = useProfile(userId);

  // Helper function to ensure avatar URL is properly formatted and fresh
  const getAvatarUrl = (avatarUrl: string | null | undefined): string | undefined => {
    if (!avatarUrl) return undefined;
    // If URL already has a timestamp, use it as-is
    if (avatarUrl.includes('?t=') || avatarUrl.includes('&t=')) {
      return avatarUrl;
    }
    const separator = avatarUrl.includes('?') ? '&' : '?';
    return `${avatarUrl}${separator}t=${Date.now()}`;
  };

  // Fetch blocked users (bidirectional)
  const { data: blockedUserIds = [] } = useQuery({
    queryKey: ["blocked-users", userId],
    queryFn: async () => {
      const { data: usersIBlocked, error: error1 } = await supabase
        .from("blocked_users")
        .select("blocked_user_id")
        .eq("user_id", userId);
      
      const { data: usersWhoBlockedMe, error: error2 } = await supabase
        .from("blocked_users")
        .select("user_id")
        .eq("blocked_user_id", userId);
      
      if (error1 || error2) throw error1 || error2;
      
      const blockedByMe = usersIBlocked?.map(b => b.blocked_user_id) || [];
      const blockedMe = usersWhoBlockedMe?.map(b => b.user_id) || [];
      return [...new Set([...blockedByMe, ...blockedMe])];
    },
  });

  // Query for pending follow requests (incoming)
  const { data: followRequests = [], isLoading: loadingRequests } = useQuery({
    queryKey: ["follow-requests", userId, blockedUserIds],
    queryFn: async () => {
      // Fetch requests
      const { data: requests, error: reqError } = await supabase
        .from("follow_requests")
        .select("id, requester_id, created_at")
        .eq("target_user_id", userId)
        .eq("status", "pending")
        .order("created_at", { ascending: false });
      if (reqError) throw reqError;
      if (!requests?.length) return [];

      // Filter out blocked users
      const filtered = requests.filter(
        (req: any) => !blockedUserIds.includes(req.requester_id)
      );

      // Filter out soft-deleted users
      const userIds = filtered.map(r => r.requester_id);
      const { data: activeUserIds } = await supabase.rpc('get_active_user_ids', {
        user_ids: userIds
      });
      
      const activeSet = new Set(activeUserIds || []);
      const activeFiltered = filtered.filter((req: any) => activeSet.has(req.requester_id));

      // Fetch profiles for these users
      const activeIds = activeFiltered.map(r => r.requester_id);
      if (!activeIds.length) return [];
      
      const { data: profiles, error: profError } = await supabase
        .from("profiles")
        .select("id, username, avatar_url")
        .in("id", activeIds);

      if (profError) {
        toast.error(`Error loading profiles: ${profError.message}`);
        throw profError;
      }

      // Merge profiles with requests
      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);
      const merged = activeFiltered.map(req => ({
        ...req,
        requester: profileMap.get(req.requester_id)
      }));
      
      // Filter out requests where profile doesn't exist (shouldn't happen but safeguard)
      const final = merged.filter(req => req.requester != null);
      
      return final;
    },
    enabled: true,
  });

  // Query for followers
  const { data: followers = [], isLoading: loadingFollowers } = useQuery({
    queryKey: ["followers", userId, blockedUserIds],
    queryFn: async () => {
      // Fetch follows
      const { data: follows, error: followError } = await supabase
        .from("follows")
        .select("id, follower_id, created_at")
        .eq("following_id", userId)
        .order("created_at", { ascending: false });

      if (followError) throw followError;
      if (!follows?.length) return [];

      // Filter out blocked users
      const filtered = follows.filter(
        (f: any) => !blockedUserIds.includes(f.follower_id)
      );

      // Filter out soft-deleted users
      const userIds = filtered.map(f => f.follower_id);
      const { data: activeUserIds } = await supabase.rpc('get_active_user_ids', {
        user_ids: userIds
      });
      
      const activeSet = new Set(activeUserIds || []);
      const activeFiltered = filtered.filter((f: any) => activeSet.has(f.follower_id));

      // Fetch profiles for these users
      const activeIds = activeFiltered.map(f => f.follower_id);
      if (!activeIds.length) return [];
      
      const { data: profiles, error: profError } = await supabase
        .from("profiles")
        .select("id, username, avatar_url")
        .in("id", activeIds);

      if (profError) throw profError;

      // Merge profiles with follows
      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);
      return activeFiltered.map(follow => ({
        ...follow,
        follower: profileMap.get(follow.follower_id)
      }));
    },
    enabled: true,
  });

  // Query for following
  const { data: following = [], isLoading: loadingFollowing } = useQuery({
    queryKey: ["following", userId, blockedUserIds],
    queryFn: async () => {
      // Fetch follows
      const { data: follows, error: followError } = await supabase
        .from("follows")
        .select("id, following_id, created_at")
        .eq("follower_id", userId)
        .order("created_at", { ascending: false });

      if (followError) throw followError;
      if (!follows?.length) return [];

      // Filter out blocked users
      const filtered = follows.filter(
        (f: any) => !blockedUserIds.includes(f.following_id)
      );

      // Filter out soft-deleted users
      const userIds = filtered.map(f => f.following_id);
      const { data: activeUserIds } = await supabase.rpc('get_active_user_ids', {
        user_ids: userIds
      });
      
      const activeSet = new Set(activeUserIds || []);
      const activeFiltered = filtered.filter((f: any) => activeSet.has(f.following_id));

      // Fetch profiles for these users
      const activeIds = activeFiltered.map(f => f.following_id);
      if (!activeIds.length) return [];
      
      const { data: profiles, error: profError } = await supabase
        .from("profiles")
        .select("id, username, avatar_url")
        .in("id", activeIds);

      if (profError) throw profError;

      // Merge profiles with follows
      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);
      return activeFiltered.map(follow => ({
        ...follow,
        following: profileMap.get(follow.following_id)
      }));
    },
    enabled: true,
  });

  // Mutation to accept follow request
  const acceptRequest = useMutation({
    mutationFn: async (requesterId: string) => {
      const { data, error } = await supabase.rpc("accept_follow_request", {
        p_requester_id: requesterId,
      });

      if (error) throw error;
      
      const result = data as { success: boolean; error?: string; requesterId?: string } | null;
      if (!result?.success) throw new Error(result?.error || "Failed to accept follow request");
      
      return { requesterId, data: result };
    },
    onSuccess: async ({ requesterId, data }) => {
      queryClient.invalidateQueries({ queryKey: ["follow-requests", userId] });
      queryClient.invalidateQueries({ queryKey: ["followers", userId] });
      toast.success(t('notifications.toast.follow_request_accepted'));
      
      // Send push notification for accepted request
      try {
        await supabase.functions.invoke('send-follow-notification', {
          body: {
            type: 'follow_accepted',
            targetUserId: requesterId,
            accepterId: userId,
          }
        });
      } catch (error) {
        console.error('Failed to send notification:', error);
        // Don't fail the whole operation if notification fails
      }
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to accept follow request");
    },
  });

  // Mutation to reject follow request
  const rejectRequest = useMutation({
    mutationFn: async (requesterId: string) => {
      const { data, error } = await supabase.rpc("reject_follow_request", {
        p_requester_id: requesterId,
      });

      if (error) throw error;
      
      const result = data as { success: boolean; error?: string } | null;
      if (!result?.success) throw new Error(result?.error || "Failed to reject follow request");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["follow-requests", userId] });
      toast.success(t('notifications.toast.follow_request_rejected'));
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to reject follow request");
    },
  });

  // Mutation to unfollow
  const unfollowUser = useMutation({
    mutationFn: async (targetUserId: string) => {
      const { data, error } = await supabase.rpc("unfollow_user", {
        p_target_user_id: targetUserId,
      });

      if (error) throw error;
      
      const result = data as { success: boolean; error?: string } | null;
      if (!result?.success) throw new Error(result?.error || "Failed to unfollow user");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["following", userId] });
      toast.success(t('notifications.toast.unfollowed_successfully'));
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to unfollow");
    },
  });

  // Mutation to remove follower
  const removeFollower = useMutation({
    mutationFn: async (followerId: string) => {
      const { data, error } = await supabase.rpc("remove_follower", {
        p_follower_id: followerId,
      });

      if (error) throw error;
      
      const result = data as { success: boolean; error?: string } | null;
      if (!result?.success) throw new Error(result?.error || "Failed to remove follower");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["followers", userId] });
      toast.success(t('notifications.toast.follower_removed'));
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to remove follower");
    },
  });

  const renderUserCard = (user: any, actionButton?: React.ReactNode) => {
    if (!user) return null;

    return (
      <div
        className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
      >
        <Avatar 
          className="h-10 w-10 cursor-pointer"
          onClick={() => user.username && navigate(`/${user.username}`)}
        >
          <AvatarImage 
            src={getAvatarUrl(user.avatar_url)}
            alt={user.username || 'User'}
            className="object-cover"
            loading="lazy"
          />
          <AvatarFallback className="bg-primary/10 text-primary">
            {user.username?.[0] || "?"}
          </AvatarFallback>
        </Avatar>
        <div 
          className="flex-1 min-w-0 cursor-pointer"
          onClick={() => user.username && navigate(`/${user.username}`)}
        >
          <p className="font-medium truncate">
            {user.username}
          </p>
          <p className="text-sm text-muted-foreground truncate">
            @{user.username}
          </p>
        </div>
        {actionButton}
      </div>
    );
  };

  const paginatedRequests = followRequests.slice(requestsPage * ITEMS_PER_PAGE, (requestsPage + 1) * ITEMS_PER_PAGE);
  const paginatedFollowers = followers.slice(followersPage * ITEMS_PER_PAGE, (followersPage + 1) * ITEMS_PER_PAGE);
  const paginatedFollowing = following.slice(followingPage * ITEMS_PER_PAGE, (followingPage + 1) * ITEMS_PER_PAGE);

  const totalRequestsPages = Math.ceil(followRequests.length / ITEMS_PER_PAGE);
  const totalFollowersPages = Math.ceil(followers.length / ITEMS_PER_PAGE);
  const totalFollowingPages = Math.ceil(following.length / ITEMS_PER_PAGE);

  // Handler to copy profile link to clipboard
  const handleShareProfile = async () => {
    // Check if profile is still loading
    if (isLoadingProfile) {
      toast.error(t('notifications.toast.profile_is_loading_please_try_again'));
      return;
    }
    
    // Check if username is available
    if (!currentUserProfile?.username) {
      console.error("[FollowsList] Cannot share profile - username is missing:", currentUserProfile);
      toast.error(t('notifications.toast.unable_to_get_profile_link_please_complete_your_pr'));
      return;
    }
    
    const profileUrl = `${window.location.origin}/${currentUserProfile.username}`;
    
    try {
      await navigator.clipboard.writeText(profileUrl);
      toast.success(t('notifications.toast.profile_link_copied_to_clipboard'));
    } catch (error) {
      console.error("[FollowsList] Failed to copy profile link:", error);
      toast.error(t('notifications.toast.failed_to_copy_link'));
    }
  };

  if (loadingRequests && loadingFollowers && loadingFollowing) {
    return (
      <Card className="p-6 mb-6">
        <p className="text-sm text-muted-foreground">{t('ui.text.loading')}</p>
      </Card>
    );
  }

  return (
    <>
      {followRequests.length > 0 && (
        <Card className="p-4 mb-4">
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={() => {
                setRequestsPage(0);
                setRequestsDialogOpen(true);
              }}
              className="flex items-center gap-2 hover:underline cursor-pointer transition-colors"
            >
              <span className="font-medium text-sm">{t('ui.text.follow_requests')}</span>
              <Badge 
                variant="destructive" 
                className="h-5 min-w-5 px-1.5 text-xs"
              >
                {followRequests.length}
              </Badge>
            </button>
          </div>
        </Card>
      )}

      {/* Requests Dialog */}
      <Dialog open={requestsDialogOpen} onOpenChange={setRequestsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Follow Requests
              {followRequests.length > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {followRequests.length}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-2">
            {followRequests.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">{t('ui.text.no_pending_follow_requests')}</p>
            ) : (
              paginatedRequests.map((request: any) => {
                const profile = request.requester;
                // Skip rendering if profile doesn't exist (user deleted)
                if (!profile) return null;
                
                return (
                  <div key={request.id}>
                    {renderUserCard(
                      profile,
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => acceptRequest.mutate(request.requester_id)}
                          disabled={acceptRequest.isPending}
                        >
                          <UserCheck className="w-4 h-4 mr-1" />
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => rejectRequest.mutate(request.requester_id)}
                          disabled={rejectRequest.isPending}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
          {totalRequestsPages > 1 && (
            <div className="flex items-center justify-between border-t pt-4 mt-4">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setRequestsPage(Math.max(0, requestsPage - 1))}
                disabled={requestsPage === 0}
              >
                {t('social.follows.previous')}
              </Button>
              <span className="text-sm text-muted-foreground">
                {t('social.follows.page')} {requestsPage + 1} {t('social.follows.of')} {totalRequestsPages}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setRequestsPage(Math.min(totalRequestsPages - 1, requestsPage + 1))}
                disabled={requestsPage >= totalRequestsPages - 1}
              >
                {t('social.follows.next')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Followers Dialog */}
      <Dialog open={followersDialogOpen} onOpenChange={setFollowersDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{t('social.follows.followers_title')} ({followers.length})</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-2">
            {followers.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">{t('social.follows.no_followers')}</p>
            ) : (
              paginatedFollowers.map((follow: any) => {
                const profile = follow.follower;
                return (
                  <div key={follow.id}>
                    {renderUserCard(
                      profile,
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeFollower.mutate(follow.follower_id)}
                        disabled={removeFollower.isPending}
                      >
                        <UserMinus className="w-4 h-4 mr-1" />
                        {t('social.follows.remove')}
                      </Button>
                    )}
                  </div>
                );
              })
            )}
          </div>
          {totalFollowersPages > 1 && (
            <div className="flex items-center justify-between border-t pt-4 mt-4">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setFollowersPage(Math.max(0, followersPage - 1))}
                disabled={followersPage === 0}
              >
                {t('social.follows.previous')}
              </Button>
              <span className="text-sm text-muted-foreground">
                {t('social.follows.page')} {followersPage + 1} {t('social.follows.of')} {totalFollowersPages}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setFollowersPage(Math.min(totalFollowersPages - 1, followersPage + 1))}
                disabled={followersPage >= totalFollowersPages - 1}
              >
                {t('social.follows.next')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Following Dialog */}
      <Dialog open={followingDialogOpen} onOpenChange={setFollowingDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{t('social.follows.following_title')} ({following.length})</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-2">
            {following.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">{t('social.follows.not_following')}</p>
            ) : (
              paginatedFollowing.map((follow: any) => {
                const profile = follow.following;
                return (
                  <div key={follow.id}>
                    {renderUserCard(
                      profile,
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => unfollowUser.mutate(follow.following_id)}
                        disabled={unfollowUser.isPending}
                      >
                        <UserMinus className="w-4 h-4 mr-1" />
                        {t('social.follows.unfollow')}
                      </Button>
                    )}
                  </div>
                );
              })
            )}
          </div>
          {totalFollowingPages > 1 && (
            <div className="flex items-center justify-between border-t pt-4 mt-4">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setFollowingPage(Math.max(0, followingPage - 1))}
                disabled={followingPage === 0}
              >
                {t('social.follows.previous')}
              </Button>
              <span className="text-sm text-muted-foreground">
                {t('social.follows.page')} {followingPage + 1} {t('social.follows.of')} {totalFollowingPages}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setFollowingPage(Math.min(totalFollowingPages - 1, followingPage + 1))}
                disabled={followingPage >= totalFollowingPages - 1}
              >
                {t('social.follows.next')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FollowsList;
